/**
 * Centralized severity colour palettes for defect/issue rendering.
 *
 * Two palettes exist historically:
 *  - `reportSeverityColors`  — used by the report viewer surface (modern, green for low)
 *  - `gallerySeverityColors` — used by the legacy gallery surface  (blue for low)
 *
 * Importing modules should pick the palette that matches their surface to avoid
 * a visible behaviour change. Future unification (single palette across surfaces)
 * should be a separate, intentional UX decision.
 */

export type SeverityKey = 'high' | 'medium' | 'low';

/** Report surface palette (AnnotatedImagePanel, IssueSidebar, FullscreenImageModal). */
export const reportSeverityColors: Record<string, string> = {
  high: '#e53e3e',
  medium: '#dd6b20',
  low: '#38a169',
};

/** Legacy gallery surface palette (BoundingBoxOverlay, CleanImageModal). */
export const gallerySeverityColors: Record<string, string> = {
  high: '#f44336', // Red
  medium: '#ff9800', // Orange
  low: '#2196f3', // Blue
};

/** Fallback color for unknown / missing severity values. */
export const UNKNOWN_SEVERITY_COLOR = '#6b7280';

/** Short uppercase severity labels (English, used as compact badges). */
export const severityShortLabels: Record<string, string> = {
  high: 'HIGH',
  medium: 'MED',
  low: 'LOW',
};

/**
 * Get a colour for a given severity from the supplied palette, falling back to
 * `UNKNOWN_SEVERITY_COLOR` when the severity is missing or unrecognised.
 */
export function getSeverityColor(
  severity: string | undefined,
  palette: Record<string, string> = reportSeverityColors,
): string {
  if (!severity) return UNKNOWN_SEVERITY_COLOR;
  return palette[severity] ?? UNKNOWN_SEVERITY_COLOR;
}
